import { formatDate, formatPrice, GreenArrow, RedArrow } from '@roc/feature-comp-tool';
import { Box, Card, CardContent, CardHeader, Checkbox, Grid, Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import SaveIcon from '@material-ui/icons/Save';
import { ComparableProperty } from '../types';
import clsx from 'clsx';
import PropertyPhotosGallery from './propertyPhotosGallery';
import { BoldCheckIcon } from './custom-icons/BoldCheckIcon';
import { ReactComponent as HouseWithPlusFilledIcon } from '../images/housewithplusfilled.svg';
import { ReactComponent as HouseWithPlusIcon } from '../images/housewithplus.svg';
import { valueOrDash } from '@roc/feature-valuation-review-v2';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      border: '1px solid #808080',
      width: '100%',
      height: '410px'
    },
    image: {
      width: '100%',
      height: 'auto',
    },
    cardText: {
      fontSize: '17px'
    },
    cardBody: {
      fontSize: '13px'
    },
    cardMedia: {
      height: '220px',
      margin: '0px 10px 0px 10px'
    },
    divider: {
      color: '#DBDBDB',
      fontSize: '18px'
    },
    icon: {
      fill: theme.palette.primary.main,
    },
    cardAppraisal: {
      border: '2px solid #90EE90',
    },
    cardDistressed: {
      border: '2px solid #FF8127',
    },
    housePlusIcon: {
      width: "16px",
      height: "16px",
      color: "#1976d2",
      marginRight: 4,
    },
    manuallyAddedIcon: {
      fill: "#000000",
      height: "28px",
      width: "28px"
    }
  },)
);

interface CompPropertyCardProps {
  subjectProperty: ComparableProperty;
  property: ComparableProperty;
  displayRentalInfo: boolean;
  savedProperty: boolean;
  checked: boolean;
  onSelect: (property: ComparableProperty) => void,
  onCheck: (property: ComparableProperty) => void
}

export const CompPropertyCard = ({ subjectProperty, property, displayRentalInfo, savedProperty, checked, onSelect, onCheck }: CompPropertyCardProps) => {

  const classes = useStyles();

  let cardClass = null;

  if (property.data.in_appraisal) {
    cardClass = classes.cardAppraisal;
  } else if (property.data.is_distressed) {
    cardClass = classes.cardDistressed;
  }

  return (
    <Card className={clsx(classes.card, cardClass)}>
      <CardHeader
        title={property.data?.address?.split(',')[0].trim()}
        titleTypographyProps={{
          style: { fontSize: '24px', fontWeight: 500 }
        }}
        action={
          <Box display="flex" flexDirection="row" gridGap="1rem" alignItems="center">
            {savedProperty && <SaveIcon className={classes.icon} />}
            <Checkbox
              checked={checked}
              onClick={() => { onCheck(property) }}
              color='primary'
            />
          </Box>
        }
      />
      <div style={{ position: "relative" }}>
        <PropertyPhotosGallery propertyId={property.property_id} images={property.data.images} height={'220px'} onClick={() => onSelect(property)} />
        {property.added_manually_comparable && (<div style={{
          position: "absolute", top: 10, left: 10,
          color: '#000000',
          backgroundColor: 'white',
          display: 'flex',
          padding: '4px',
          borderRadius: '4px',
          justifyContent: "center",
          alignItems: "center"
        }}>
          <HouseWithPlusIcon className={classes.manuallyAddedIcon} />
        </div>)}
        {!property.added_manually_comparable && (<div style={{
          position: "absolute", top: 10, left: 10,
          color: '#000000',
          backgroundColor: 'white',
          display: 'inline',
          padding: '0px 12px',
          borderRadius: '4px'
        }}>
          <Typography gutterBottom style={{ fontSize: '24px', margin: '0px' }}>
            {property.number}
          </Typography>
        </div>)}


        <div style={{
          position: "absolute", top: 10, right: 10,
          color: '#000000',
          backgroundColor: 'white',
          display: 'inline',
          padding: '5px',
          borderRadius: '4px'
        }}>
          <Typography gutterBottom style={{ fontSize: '32px', margin: '0px' }}>
            {formatPrice(property.data.price, property.data.rent, displayRentalInfo)}{displayRentalInfo && <strong><span style={{ fontSize: '16px' }}>/mo</span></strong>}
          </Typography>
        </div>
      </div>
      <CardContent onClick={() => onSelect(property)}>
        <Grid container spacing={1}>
          <Grid item xs={3} style={{ padding: '5px 0px' }}>
            <Typography align="left" className={classes.cardText}>
              <strong>{valueOrDash(property.data.bedrooms)}</strong> <span style={{ fontSize: '14px' }}>bd</span>{getArrowSymbol(subjectProperty, 'bedrooms', property)} <span className={classes.divider}>|</span>
            </Typography>
          </Grid>
          <Grid item xs={3} style={{ padding: '5px 0px' }}>
            <Typography align="left" className={classes.cardText}>
              <strong>{valueOrDash(property.data.full_bathrooms)}</strong> <span style={{ fontSize: '14px' }}>ba</span>{getArrowSymbol(subjectProperty, 'full_bathrooms', property)} <span className={classes.divider}>|</span>
            </Typography>

          </Grid>
          <Grid item xs={3} style={{ padding: '5px 0px' }}>
            <Typography align="left" className={classes.cardText}>
              <strong>{valueOrDash(property.data.half_bathrooms)}</strong> <span style={{ fontSize: '14px' }}>hba</span>{getArrowSymbol(subjectProperty, 'half_bathrooms', property)} <span className={classes.divider}>|</span>
            </Typography>

          </Grid>
          <Grid item xs={3} style={{ padding: '5px 0px' }}>
            <Typography align="right" className={classes.cardText}>
              <strong>{valueOrDash(property.data.area)}</strong> <span style={{ fontSize: '12px' }}>sqft</span>{getArrowSymbol(subjectProperty, 'area', property)}
            </Typography>
          </Grid>
          {displayRentalInfo ?
            (<Grid item xs={6}>
              <Typography align="left" className={classes.cardBody}>
                Rent Date <strong>{formatDate(property.data.rent_date)}</strong>
              </Typography>
            </Grid>) : (<Grid item xs={6}>
              <Typography align="left" className={classes.cardBody}>
                Last sold on <strong>{formatDate(property.data.sale_date)}</strong>
              </Typography>
            </Grid>)}

          <Grid item xs={3}>
            <Typography align="center" className={classes.cardBody}>
              Built <strong>{valueOrDash(property.data.year_built)}</strong>
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography align="right" className={classes.cardBody}>
              <strong>{`${property.similarity.mi_distance != null ? property.similarity.mi_distance.toFixed(2) : '-'}`} mi</strong> away
            </Typography>
          </Grid>
          <Grid container spacing={1}>
            {property.added_manually_comparable && (
              <Grid item xs={6}>
                <Box display="flex" alignItems="center" justifyContent="flex-start">
                  <HouseWithPlusFilledIcon className={classes.housePlusIcon} />
                  <Typography align="left" className={classes.cardBody}>
                    <strong>Added Comparable</strong>
                  </Typography>
                </Box>
              </Grid>
            )}

            {(property.data.in_appraisal && !property.added_manually_comparable) && (
              <Grid item xs={6}>
                <Box display="flex" alignItems="center" justifyContent="flex-start">
                  <BoldCheckIcon style={{
                    fontSize: 16,
                    color: '#90EE90',
                    marginRight: 4,
                  }} />
                  <Typography align="left" className={classes.cardBody}>
                    <strong>Appraisal Property</strong>
                  </Typography>
                </Box>
              </Grid>
            )}
            {(!property.data.in_appraisal && property.data.is_distressed && !property.added_manually_comparable) && (
              <Grid item xs={6}>
                <Box display="flex" alignItems="center" justifyContent="flex-start">
                  <ErrorOutlineIcon style={{
                    fontSize: 16,
                    color: '#FF8127',
                    marginRight: 4,
                  }} />
                  <Typography align="left" className={classes.cardBody}>
                    <strong>Distressed Property</strong>
                  </Typography>
                </Box>
              </Grid>
            )}
            <Grid item xs={(property.data.in_appraisal || property.added_manually_comparable) ? 6 : 12}>
              <Typography align="right" className={classes.cardBody}>
                <strong>{property.data.days_on_market ?? '-'}</strong> DOM
              </Typography>
            </Grid>
          </Grid>

        </Grid>
      </CardContent>
    </Card>);
}

const getArrowSymbol = (subjectProperty, key, property) => {
  if (subjectProperty.data[key] && property.data[key]) {
    if (subjectProperty.data[key] > property.data[key]) {
      return <RedArrow />;
    } else if (subjectProperty.data[key] < property.data[key]) {
      return <GreenArrow />;
    }
  }
  return null;
}